import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getUserSettings, updateUserSettings } from '../utils/api.js'
import { useFetch } from '../hooks/useFetch.js'
import { hapticImpact, hapticNotification } from '../utils/telegram.js'

const DEFAULT_SETTINGS = {
  preferredLanguage: 'ru-RU',
  preferredDateFormat: 'event_title',
  checkFrequencyValue: 15,
  checkFrequencyUnit: 'minutes'
}

export default function UserSettingsPage() {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState(null)
  const { data, loading, error } = useFetch(() => getUserSettings())

  const [form, setForm] = useState(DEFAULT_SETTINGS)

  useEffect(() => {
    if (data?.data) {
      setForm(data.data)
    }
  }, [data])

  const handleSave = async (e) => {
    e.preventDefault()
    hapticImpact('light')
    const frequencyValue = Number.parseInt(form.checkFrequencyValue, 10)
    if (!Number.isInteger(frequencyValue) || frequencyValue < 1) {
      setAlert({ type: 'error', text: 'Frequency value must be a positive integer' })
      return
    }
    setSaving(true)
    setAlert(null)
    try {
      const response = await updateUserSettings({
        ...form,
        checkFrequencyValue: frequencyValue
      })
      if (response?.data?.data) {
        setForm(prev => ({ ...prev, ...response.data.data }))
      }
      hapticNotification('success')
      setAlert({ type: 'success', text: 'Settings saved' })
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to save settings' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 16, marginBottom: 8, width: 'auto' }}
        onClick={() => navigate('/settings')}
      >
        ← Back
      </button>

      <h2 className="section-title">User Preferences</h2>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.text}
          <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setAlert(null)}>✕</button>
        </div>
      )}
      {loading && <div className="status-message"><div className="loading-spinner" /></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && (
        <form onSubmit={handleSave} className="card" noValidate>
          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              className="form-input"
              value={form.preferredLanguage}
              onChange={e => setForm(prev => ({ ...prev, preferredLanguage: e.target.value }))}
            >
              <option value="ru-RU">Русский (Russian)</option>
              <option value="en-GB">English</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Date and time format</label>
            <select
              className="form-input"
              value={form.preferredDateFormat}
              onChange={e => setForm(prev => ({ ...prev, preferredDateFormat: e.target.value }))}
            >
              <option value="event_title">ДД месяц, день недели ЧЧ:ММ (e.g. 01 июня, суббота 19:00)</option>
              <option value="short_datetime">Short date/time</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Automatic checks frequency</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                aria-label="Automatic checks frequency value"
                type="number"
                min="1"
                step="1"
                value={form.checkFrequencyValue}
                onChange={e => setForm(prev => ({ ...prev, checkFrequencyValue: e.target.value }))}
                style={{ flex: 1 }}
              />
              <select
                className="form-input"
                aria-label="Automatic checks frequency unit"
                value={form.checkFrequencyUnit}
                onChange={e => setForm(prev => ({ ...prev, checkFrequencyUnit: e.target.value }))}
                style={{ flex: 1 }}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </form>
      )}
    </div>
  )
}
